import * as React from "react";
import { observer } from "mobx-react";
import { Input, Button, Col, Row, Form, Select, Tag } from "antd";
import TaskTemplateVisualStore from "../stores/TaskTemplatesVisualStore";
import { Utils } from "../../common/misc/Utils";
import {
    EditOutlined,
    MinusCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import SubtaskTemplateDialog from "./SubtaskTemplateDialog";
import { Editor } from "@progress/kendo-react-editor";
import { AdvancedToolbar } from "../../common/components/EditorToolbar";
import AppModal from "../../common/components/AppModal";
import { getSelectableTaskTypeId } from "../../task_types/helpers/taskTypeSelection";
type Props = {
    store: TaskTemplateVisualStore;
};
const FormItem = Form.Item;

const TaskTemplateEditDialog: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();
    const description = Form.useWatch("description", form) ?? "";

    const renderTaskTypeLabel = (name: string, isSystemDefault?: boolean) => (
        <>
            {name}
            {isSystemDefault ? (
                <Tag className="alpha-info-tag" style={{ marginLeft: 8 }}>
                    System managed
                </Tag>
            ) : null}
        </>
    );

    React.useEffect(() => {
        if (store.selectedTaskTemplateId) {
            // Telerik editor breaks if value is null
            store.selectedTemplate!.description =
                store.selectedTemplate!.description || "";
            form.setFieldsValue({
                ...store.selectedTemplate,
                taskType: getSelectableTaskTypeId(
                    store.taskTypes,
                    store.selectedTemplate?.taskType,
                ),
            });
        } else if (store.selectedTaskTemplateId === null) {
            form.setFieldsValue({ projectId: store.selectedProjectId });
            store.setSelectedTaskTypeId(undefined);
        }

        if (store.currentProjectId !== undefined && store.selectedProjectId) {
            store.loadTaskTypesForProject(store.selectedProjectId).then(() => {
                const selectedTaskTypeId = getSelectableTaskTypeId(
                    store.taskTypes,
                    store.selectedTemplate?.taskType,
                );

                if (store.selectedTemplate) {
                    form.setFieldsValue({
                        taskType: selectedTaskTypeId,
                    });
                    store.setSelectedTaskTypeId(selectedTaskTypeId);
                } else {
                    form.setFieldsValue({ taskType: undefined });
                    store.setSelectedTaskTypeId(undefined);
                }
            });
        }
    }, [store, form, store.selectedTaskTemplateId]);

    const handleSubmit = () => {
        form.validateFields()
            .then((values) => {
                if (store.selectedTaskTemplateId) {
                    store.updateTaskTemplate(values);
                } else {
                    store.createTaskTemplate(values);
                }

                handleClose();
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleClose = () => {
        form.resetFields();
        store.selectTemplate(undefined);
    };

    const getSubtaskTemplate = (index: number) =>
        form.getFieldValue(["subTasks", index]);

    const taskTemplateNameValidator = (
        rule: unknown,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callback: any,
    ) => {
        const taskTemplates = store!.taskTemplates.map((p) =>
            p.name.toLowerCase(),
        );
        if (
            value &&
            taskTemplates.includes(value.toLowerCase()) &&
            store.selectedTemplate?.name.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Template with the same name already exists");
        } else {
            callback();
        }
    };

    const taskTemplateCodeValidator = (
        rule: unknown,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callback: any,
    ) => {
        const taskTemplates = store!.taskTemplates.map((p) =>
            p.templateCode.toLowerCase(),
        );
        if (
            value &&
            taskTemplates.includes(value.toLowerCase()) &&
            store.selectedTemplate?.templateCode.toLowerCase() !==
                value.toLowerCase()
        ) {
            callback("Template with the same code already exists");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            title={
                store.selectedTaskTemplateId ? "Edit template" : "New template"
            }
            open={store.selectedTaskTemplateId !== undefined}
            className="alpha-modal"
            closable={false}
            width={574}
            centered
            cancelButtonTitle="Cancel"
            onCancelClick={handleClose}
            cancelButtonProps={{
                "data-id": "button-edit-task-template-cancel",
                size: undefined,
            }}
            submitButtonTitle={store.selectedTaskTemplateId ? "Update" : "Save"}
            submitButtonProps={{
                "data-id": "button-edit-task-template-Save",
                form: "task-template-form",
                size: undefined,
            }}
        >
            <div data-id="update-task-template-dialog-form">
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    className="alpha-form"
                    id="task-template-form"
                    layout="vertical"
                >
                    <FormItem name="projectId" noStyle>
                        <Input type="hidden" />
                    </FormItem>
                    <Row gutter={8}>
                        <Col span={18}>
                            <FormItem
                                name="name"
                                colon={false}
                                className="dialog-field"
                                label={
                                    <span className="dialog-field-label">
                                        Template name
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: "Template name is required",
                                    },
                                    { validator: taskTemplateNameValidator },
                                ]}
                            >
                                <Input data-id="input-Name" />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem
                                name="templateCode"
                                colon={false}
                                className="dialog-field"
                                label={
                                    <span className="dialog-field-label">
                                        Template code
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: "Template code is required",
                                    },
                                    { validator: taskTemplateCodeValidator },
                                ]}
                            >
                                <Input
                                    data-id="input-template-code"
                                    disabled={!!store.selectedTaskTemplateId}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    <FormItem
                        name="title"
                        colon={false}
                        className="dialog-field"
                        label={
                            <span className="dialog-field-label">
                                Task title
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: "Task title is required",
                            },
                        ]}
                    >
                        <Input data-id="input-title" />
                    </FormItem>
                    <Row gutter={24} style={{ marginTop: 5, paddingTop: 5 }}>
                        {store.shouldShowTaskTypeSelector && (
                            <Col span={12}>
                                <FormItem
                                    name="taskType"
                                    colon={false}
                                    className="dialog-field"
                                    label={
                                        <span className="dialog-field-label">
                                            Task type
                                        </span>
                                    }
                                >
                                    <Select
                                        options={
                                            store.customTaskTypes.map((p) => ({
                                                label: renderTaskTypeLabel(
                                                    p.name,
                                                    p.isSystemDefault,
                                                ),
                                                value: p.id,
                                                customFilterValue: p.name,
                                            })) || []
                                        }
                                        placeholder="General"
                                        filterOption={Utils.filterOption}
                                        showSearch
                                        onChange={store.setSelectedTaskTypeId}
                                        onClear={() =>
                                            store.setSelectedTaskTypeId(
                                                undefined,
                                            )
                                        }
                                        allowClear
                                    />
                                </FormItem>
                            </Col>
                        )}
                        <Col span={store.shouldShowTaskTypeSelector ? 12 : 24}>
                            <FormItem
                                name="defaultPriority"
                                colon={false}
                                className="dialog-field"
                                label={
                                    <span className="dialog-field-label">
                                        Task priority
                                    </span>
                                }
                            >
                                <Select
                                    options={Utils.getTaskPrioritiesForDropdown()}
                                    placeholder="Select task priority"
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    <FormItem
                        name="description"
                        // The editor owns its value; Form.Item only captures its HTML.
                        getValueProps={() => ({})}
                        getValueFromEvent={(event) => event.html}
                        colon={false}
                        className="dialog-field"
                        style={{ marginBottom: 5 }}
                        label={
                            <span className="dialog-field-label">
                                Task description
                            </span>
                        }
                    >
                        <Editor
                            key={store.selectedTaskTemplateId || "new"}
                            tools={AdvancedToolbar}
                            className="telerik-editor-description"
                            defaultEditMode="div"
                            value={description}
                        />
                    </FormItem>
                    <FormItem
                        className="dialog-field"
                        label={<span className="dialog-field-label"> </span>}
                        style={{ marginBottom: 0 }}
                    >
                        <Form.List name="subTasks">
                            {(subTasks, { add, remove }) => (
                                <>
                                    {subTasks.map((_, index) => (
                                        <div
                                            key={index}
                                            style={{ display: "flex" }}
                                        >
                                            <Form.Item
                                                name={[index, "title"]}
                                                className="dialog-field"
                                                style={{
                                                    flex: 1,
                                                    marginRight: "8px",
                                                }}
                                            >
                                                <Input disabled />
                                            </Form.Item>
                                            <Form.Item
                                                name={[index, "description"]}
                                                noStyle
                                            >
                                                <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item>
                                                <EditOutlined
                                                    className="alpha-antd-icon"
                                                    onClick={() =>
                                                        store.setSelectedSubtaskTemplate(
                                                            getSubtaskTemplate(
                                                                index,
                                                            ),
                                                            index,
                                                        )
                                                    }
                                                />
                                            </Form.Item>
                                            <Form.Item>
                                                <MinusCircleOutlined
                                                    className="alpha-antd-icon no-mragin"
                                                    onClick={() =>
                                                        remove(index)
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                    ))}
                                    <Button
                                        type="dashed"
                                        size="small"
                                        onClick={() =>
                                            store.setSelectedSubtaskTemplate({
                                                title: "",
                                                description: undefined,
                                            })
                                        }
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Add subtask
                                    </Button>
                                    <SubtaskTemplateDialog
                                        store={store}
                                        addCallback={add}
                                        parentForm={form}
                                    />
                                </>
                            )}
                        </Form.List>
                    </FormItem>
                </Form>
            </div>
        </AppModal>
    );
};

export default observer(TaskTemplateEditDialog);
